import { ServiceWorkerAPI } from "../ServiceWorker/api";
import type { OffscreenAPI } from "../Pages/offscreen/api";
import type * as DevToolsPanelAPI from "../Pages/devtools/api";
import { InstrumentationEvent } from "../lib/Events/InstrumentationEvent";
import { ExtensionSettingsPartial } from "../lib/MultiWorld/ExtensionSettings";
import { DbRecordedEvent, DbTabBeingRecorded, UUID } from "../State/EvidenceDB/DatabaseTypes";
import { DbSignedLicense } from "../State/EvidenceDB/License";


export const CustomEventName = {
  ForwardMessageFromMainWorldToServiceWorker: "ForwardMessageFromMainWorldToServiceWorker",
  ForwardMessageFromServiceWorkerToMainWorld: "ForwardMessageFromServiceWorkerToMainWorld",
  MainWorldRequestsExtensionSettingsFromIsolatedWorld: "MainWorldRequestsSettingsFromIsolatedWorld",
  IsolatedWorldSendsExtensionSettingsToMainWorld: "IsolatedWorldSendsSettingsToMainWorld",
} as const;
// export const CustomEventName_ForwardMessageFromMainWorldToIsolatedRuntime = "ForwardMessageFromMainWorldToIsolatedRuntime";
// export const CustomEventName_ForwardMessageFromIsolatedRuntimeToMainWorld = "ForwardMessageFromIsolatedRuntimeToMainWorld";

export interface MessageBase<T extends string> {
  messageType: T;
}

export const isMessageBase = <T extends string>(names: readonly T[], message: unknown): message is MessageBase<T> =>
  message != null && typeof message === "object" &&
        ("messageType" in message) && (typeof message.messageType === "string") &&
        ((names as readonly string[]).includes(message["messageType"]))

export interface AnnouncementEventAddedOrUpdatedInDatabase {
  recordingUuid: UUID;
  eventsInfo: {
    eventIndex: number;
    category: DbRecordedEvent["category"];
    eventName: DbRecordedEvent["eventName"];
  }[];
}

export const AnnouncementLicenseDeletedFromDatabaseMessageName = "announcingLicenseDeletedFromDatabase";
export interface AnnouncementLicenseDeletedFromDatabase extends MessageBase<typeof AnnouncementLicenseDeletedFromDatabaseMessageName> {
  licenseUuid: UUID;
}
export const AnnouncementLicenseAddedToDatabaseMessageName = "announcingLicenseAddedToDatabase";
export interface AnnouncementLicenseAddedToDatabase extends MessageBase<typeof AnnouncementLicenseAddedToDatabaseMessageName> {
  signedLicense: DbSignedLicense;
}


export const AnnouncementNewEventAddedToDatabaseMessageName = "announcingNewEventsAddedInDatabase";
export interface AnnouncementNewEventsAddedToDatabase extends MessageBase<typeof AnnouncementNewEventAddedToDatabaseMessageName>, AnnouncementEventAddedOrUpdatedInDatabase {}

export const AnnouncementEventUpdatedInDatabaseMessageName = "announcingEventUpdatedInDatabase";
export interface AnnouncementEventUpdatedInDatabase extends MessageBase<typeof AnnouncementEventUpdatedInDatabaseMessageName>, AnnouncementEventAddedOrUpdatedInDatabase {}

export const AnnouncementRecordingCreatedMessageName = "announcingRecordingCreated";
export interface AnnouncementRecordingCreated extends MessageBase<typeof AnnouncementRecordingCreatedMessageName> {
  recordingUuid: UUID;
}
export const AnnouncementRecordingUpdatedMessageName = "announcingRecordingUpdated";
export interface AnnouncementRecordingUpdated extends MessageBase<typeof AnnouncementRecordingUpdatedMessageName> {
  recordingUuid: UUID;
}
export const AnnouncementRecordingDeletedMessageName = "announcingRecordingDeleted";
export interface AnnouncementRecordingDeleted extends MessageBase<typeof AnnouncementRecordingDeletedMessageName> {
  recordingUuid: UUID;
}
export const AnnouncementTabStartedRecordingMessageName = "announcingTabStartedRecording";
export interface AnnouncementTabStartedRecording extends MessageBase<typeof AnnouncementTabStartedRecordingMessageName>, DbTabBeingRecorded {
}
export const AnnouncementTabStoppedRecordingMessageName = "announcingTabStoppedRecording";
export interface AnnouncementTabStoppedRecording extends MessageBase<typeof AnnouncementTabStoppedRecordingMessageName> {
  tabId: number;
}

export const AnnouncementExtensionSettingsUpdatedMessageName = "announcingExtensionSettingsUpdated";
export interface AnnouncementExtensionSettingsUpdated extends MessageBase<typeof AnnouncementExtensionSettingsUpdatedMessageName> {
  // recordingUuid: UUID;
}

const RecordingAnnouncementMessageNames = [
  AnnouncementRecordingCreatedMessageName,
  AnnouncementRecordingUpdatedMessageName,
  AnnouncementRecordingDeletedMessageName,
  AnnouncementTabStartedRecordingMessageName,
  AnnouncementTabStoppedRecordingMessageName,
 ] as const;
 export type RecordingAnnouncementMessageName = typeof RecordingAnnouncementMessageNames[number];

 const EventAnnouncementMessageNames = [
  AnnouncementNewEventAddedToDatabaseMessageName,
  AnnouncementEventUpdatedInDatabaseMessageName,
 ] as const;
 export type EventAnnouncementMessageName = typeof EventAnnouncementMessageNames[number];

 const SettingsAnnouncementMessageNames = [
  AnnouncementExtensionSettingsUpdatedMessageName,
 ] as const;

 const LicenseMessageNames = [
  AnnouncementLicenseAddedToDatabaseMessageName,
  AnnouncementLicenseDeletedFromDatabaseMessageName,
 ] as const;

const AnnouncementMessageNames = [
  ...RecordingAnnouncementMessageNames,
  ...EventAnnouncementMessageNames,
  ...SettingsAnnouncementMessageNames,
  ...LicenseMessageNames,
 ] as const;
  export type AnnouncementMessageName = typeof AnnouncementMessageNames[number];

export type AnnouncementMessage = never |
  AnnouncementLicenseAddedToDatabase |
  AnnouncementLicenseDeletedFromDatabase |
  AnnouncementExtensionSettingsUpdated |
  AnnouncementNewEventsAddedToDatabase | 
  AnnouncementEventUpdatedInDatabase |
  AnnouncementRecordingCreated |
  AnnouncementRecordingUpdated |
  AnnouncementRecordingDeleted |
  AnnouncementTabStartedRecording |
  AnnouncementTabStoppedRecording |
  never;
  

export const MainWorldApiMessageName = {
  updateSettings: "updateSettings",
} as const;

export interface MessageToMainWorldBase<T extends string> extends MessageBase<T> {}

export interface MessageToMainWorldUpdateSettings extends MessageToMainWorldBase<typeof MainWorldApiMessageName.updateSettings> {
  messageType: typeof MainWorldApiMessageName.updateSettings;
  settings: ExtensionSettingsPartial;
  tabId: number;
  isRecording: boolean;
}

export type MessageToMainWorld = MessageToMainWorldUpdateSettings;

export const SharedAPICommandName = {
  recordInstrumentationEvent: "recordInstrumentationEvent",
} as const;

export const ServiceWorkerAPICommandName = {
  ...SharedAPICommandName,
  addDevToolsOnRequestFinishedEvent: "addDevToolsOnRequestFinishedEvent",
  startRecording: "startRecording",
  startCrawl: "startCrawl",
  setCrawlState: "setCrawlState",
  getCrawlState: "getCrawlState",
  addTabToRecording: "addTabToRecording",
  stopRecording: "stopRecording",
  resumeRecording: "resumeRecording",
} as const;

export const OffscreenAPICommandName = {
  startScreenRecording: "startScreenRecording",
  stopScreenRecording: "stopScreenRecording",
  pauseScreenRecording: "pauseScreenRecording",
  resumeScreenRecording: "resumeScreenRecording",
  flushScreenRecording: "flushScreenRecording",
} as const;
export type OffscreenAPICommandName = typeof OffscreenAPICommandName[keyof typeof OffscreenAPICommandName];

export const DevToolsPanelAPICommandName = {
  getAndSaveHAR: "getAndSaveHAR",
} as const;
export type DevToolsPanelAPICommandName = typeof DevToolsPanelAPICommandName[keyof typeof DevToolsPanelAPICommandName];

// export const APICommandName = {
//   ...SharedAPICommandName,
//   ...ServiceWorkerAPICommandName,
//   ...OffscreenAPICommandName,
// }
// export type ApiCommandName = typeof APICommandName;


export const ContentSourceSearchWorkerResultsKey = "ContentSourceSearchWorkerResults";

/**
 * Shared API messages
 */
export interface InstrumentationEventMessage {
  messageType: typeof SharedAPICommandName.recordInstrumentationEvent;
  instrumentationEvent: InstrumentationEvent;
}

/**
 * Service Worker API messages
 */
export type StartCrawlApiRequestMessage = Parameters<typeof ServiceWorkerAPI.startCrawl>[0] & {
  messageType: typeof ServiceWorkerAPICommandName.startCrawl;
}
export type SetCrawlStateApiRequestMessage = Parameters<typeof ServiceWorkerAPI.setCrawlState>[0] & {
  messageType: typeof ServiceWorkerAPICommandName.setCrawlState;
}
export type getCrawlStateApiRequestMessage = Parameters<typeof ServiceWorkerAPI.getCrawlState>[0] & {
  messageType: typeof ServiceWorkerAPICommandName.getCrawlState;
}
export type StartRecordingApiRequestMessage = Parameters<typeof ServiceWorkerAPI.startNewRecording>[0] & {
  messageType: typeof ServiceWorkerAPICommandName.startRecording;
}
export type AddTabToRecordingApiRequestMessage = Parameters<typeof ServiceWorkerAPI.addTabToRecording>[0] & {
  messageType: typeof ServiceWorkerAPICommandName.addTabToRecording;
}
export type ResumeRecordingApiRequestMessage = Parameters<typeof ServiceWorkerAPI.resumeRecording>[0] & {
  messageType: typeof ServiceWorkerAPICommandName.resumeRecording;
}
export type StopRecordingApiRequestMessage = Parameters<typeof ServiceWorkerAPI.stopRecording>[0] & {
  messageType: typeof ServiceWorkerAPICommandName.stopRecording;
}

/**
 * Offscreen API messages
 */
export type StartScreenRecordingOffscreenApiRequestMessage = Parameters<typeof OffscreenAPI.startScreenRecording>[0] & {
  messageType: typeof OffscreenAPICommandName.startScreenRecording;
}
export type StopScreenRecordingOffscreenApiRequestMessage = Parameters<typeof OffscreenAPI.stopScreenRecording>[0] & {
  messageType: typeof OffscreenAPICommandName.stopScreenRecording;
}
export type PauseScreenRecordingOffscreenApiRequestMessage = Parameters<typeof OffscreenAPI.pauseScreenRecording>[0] & {
  messageType: typeof OffscreenAPICommandName.pauseScreenRecording;
}
export type ResumeScreenRecordingOffscreenApiRequestMessage = Parameters<typeof OffscreenAPI.stopScreenRecording>[0] & {
  messageType: typeof OffscreenAPICommandName.resumeScreenRecording;
}
export type FlushScreenRecordingOffscreenApiRequestMessage = Parameters<typeof OffscreenAPI.flushScreenRecording>[0] & {
  messageType: typeof OffscreenAPICommandName.flushScreenRecording;
}

/**
 * DevTools API messages
 */
export type GetAndSaveHARDevToolsApiRequestMessage = Parameters<typeof DevToolsPanelAPI.getAndSaveHAR>[0] &  {
  messageType: typeof DevToolsPanelAPICommandName.getAndSaveHAR;
}

export type ServiceWorkerAPIRequestMessage =
  InstrumentationEventMessage |
  AddTabToRecordingApiRequestMessage |
  StartCrawlApiRequestMessage |
  SetCrawlStateApiRequestMessage |
  getCrawlStateApiRequestMessage |
  StartRecordingApiRequestMessage |
  ResumeRecordingApiRequestMessage |
  StopRecordingApiRequestMessage |
  never;


export type OffscreenAPIRequestMessage =
  StartScreenRecordingOffscreenApiRequestMessage |
  StopScreenRecordingOffscreenApiRequestMessage |
  PauseScreenRecordingOffscreenApiRequestMessage |
  ResumeScreenRecordingOffscreenApiRequestMessage |
  FlushScreenRecordingOffscreenApiRequestMessage |
  never;

export type DevToolsPanelAPIRequestMessage =
  GetAndSaveHARDevToolsApiRequestMessage |
  never;

export type ApiRequestMessage =
  ServiceWorkerAPIRequestMessage |
  InstrumentationEventMessage |
  DevToolsPanelAPIRequestMessage |
  never;

export type Message = ApiRequestMessage | AnnouncementMessage;

export const isMessage = <T extends string>(names: readonly T[]) => (message: unknown): message is MessageBase<T> & Message =>
  isMessageBase(names, message);
  
export const isOffscreenAPIRequestMessage = isMessage(Object.values(OffscreenAPICommandName));
export const isDevToolsPanelAPIRequestMessage  = isMessage(Object.values(DevToolsPanelAPICommandName));

export const isEventAnnouncementMessage = isMessage(EventAnnouncementMessageNames);
export const isRecordingAnnouncementMessage = isMessage(RecordingAnnouncementMessageNames);

export const isAnnouncementMessage = isMessage(AnnouncementMessageNames);

export const isServiceWorkerAPIRequestMessage = isMessage(Object.values(ServiceWorkerAPICommandName));

export const isAnnouncementSettingsUpdated = isMessage([AnnouncementExtensionSettingsUpdatedMessageName]);